import RootStore from "store/rootStore";
import { fabric } from "fabric";
import { min, max } from "lodash";
import computeBorderedPointCoords from "pages/labelerSegmentation2/tools/computeBorderedPointCoords";

const BORD_DETECT_LIMIT = 50;

const mouseMoveHandler = (e: fabric.IEvent, store: RootStore) => {
  const {
    labelerSegmentationStore: { isReadOnly },
    labelerSegmentationUiStore: {
      isZoomIn,
      moveInZoom,
      borderMode,
      selectMode,
      addMode,
      deleteMode,
      imageLoadError,
    },
  } = store;

  if (isReadOnly || imageLoadError) {
    if (isZoomIn && moveInZoom) {
      moveInZoomHandler(e, store);
    }
    return;
  }

  if (isZoomIn && moveInZoom) {
    moveInZoomHandler(e, store);
    return;
  }

  if (borderMode) {
    borderModeHandler(e, store);
    return;
  }

  // if (selectMode && !deleteMode) {
  if (addMode) {  
    addPointHandler(e, store);
  }
};

// HANDLERS //////////////////////////////////

const addPointHandler = (e: fabric.IEvent, store: RootStore) => {
  const {
    labelerSegmentationUiStore: { activePolygon },
    fabricSegmentationStore: { canvas, setNewPoint, removeNewPoint },
  } = store;

  // const activePolygon = canvas.getActiveObject();

  if (!activePolygon) {
    removeNewPoint();
    return;
  }

  let { x, y } = canvas.getPointer(e.e);
  const lineContains = getSegmentPointsNearMouse(activePolygon, x, y, 15);

  if (lineContains.length === 0) {
    removeNewPoint();
    return;
  }

  const [leftPoint, rightPoint, newIndex] = lineContains;
  const pointInCenter = _computeNewPointCoordinates(lineContains);

  const pointObject = new fabric.Circle({
    radius: 5,
    fill: activePolygon.fill,
    selectable: false,
    originY: "center",
    originX: "center",
    hoverCursor: "copy",
    top: pointInCenter.y,
    left: pointInCenter.x,
  });

  removeNewPoint();
  setNewPoint({ object: pointObject, leftPoint, rightPoint, newIndex });

  canvas.add(pointObject);
  canvas.requestRenderAll();

  const isCloseToCenter =
    Math.abs(pointInCenter.x - x) < 5 && Math.abs(pointInCenter.y - y) < 50;

  canvas.setCursor(isCloseToCenter ? "copy" : "default");
};

const moveInZoomHandler = (e: fabric.IEvent, store: RootStore) => {
  const {
    fabricSegmentationStore: { canvas },
    labelerSegmentationUiStore: { scaling },
  } = store;

  canvas.setCursor("move");
  const { tl, tr, br } = canvas.calcViewportBoundaries();
  let dplX;
  let dplY;
  const eventMouse = e.e as MouseEvent;
  if (eventMouse.movementX > 0) {
    // vers la droite
    dplX = tl.x - eventMouse.movementX > 0 ? eventMouse.movementX : 0;
  } else {
    // vers la gauche
    dplX =
      tr.x - eventMouse.movementX <
      store.labelerSegmentationUiStore.scaling.newWidth
        ? eventMouse.movementX
        : 0;
  }

  if (eventMouse.movementY > 0) {
    // vers le bas
    dplY = tl.y - eventMouse.movementY > 0 ? eventMouse.movementY : 0;
  } else {
    // vers le haut
    dplY =
      br.y - eventMouse.movementY < scaling.newHeight
        ? eventMouse.movementY
        : 0;
  }

  var delta = new fabric.Point(dplX, dplY);
  canvas.relativePan(delta);
};

const borderModeHandler = (e: fabric.IEvent, store: RootStore) => {
  const {
    fabricSegmentationStore: {
      canvas,
      borderModeIndicator,
      setBorderModeIndicator,
      removeBorderModeIndicator,
    },
    labelerSegmentationUiStore: { scaling, isZoomIn, moveInZoom, borderMode },
  } = store;

  // determiner le bord
  const LINE_LENGTH = BORD_DETECT_LIMIT / 4;
  const borderSide = borderModeIndicator
    ? borderModeIndicator.get("borderSide")
    : null;
  const coords = computeBorderedPointCoords(
    canvas.getPointer(e.e),
    scaling,
    BORD_DETECT_LIMIT,
    borderSide
  );
  const fill = "#FFF";
  let indicatorCoords;
  switch (coords.border) {
    case "bottom":
      indicatorCoords = [coords.x, coords.y, coords.x, coords.y - LINE_LENGTH];
      break;
    case "top":
      indicatorCoords = [coords.x, coords.y, coords.x, coords.y + LINE_LENGTH];
      break;
    case "right":
      indicatorCoords = [coords.x, coords.y, coords.x - LINE_LENGTH, coords.y];
      break;
    case "left":
      indicatorCoords = [coords.x, coords.y, coords.x + LINE_LENGTH, coords.y];
      break;
    case "rightBottom":
      indicatorCoords = [
        coords.x,
        coords.y,
        coords.x - LINE_LENGTH,
        coords.y - LINE_LENGTH,
      ];
      break;
    case "leftBottom":
      indicatorCoords = [
        coords.x,
        coords.y,
        coords.x + LINE_LENGTH,
        coords.y - LINE_LENGTH,
      ];
      break;
    case "rightTop":
      indicatorCoords = [
        coords.x,
        coords.y,
        coords.x - LINE_LENGTH,
        coords.y + LINE_LENGTH,
      ];
      break;
    case "leftTop":
      indicatorCoords = [
        coords.x,
        coords.y,
        coords.x + LINE_LENGTH,
        coords.y + LINE_LENGTH,
      ];
      break;
  }

  if (!indicatorCoords) {
    removeBorderModeIndicator();
    return;
  }

  if (!borderModeIndicator) {
    const newBorderModeIndicator = new fabric.Line(indicatorCoords, {
      fill,
      stroke: fill,
      strokeWidth: 2,
      opacity: 1,
      selectable: false,
      evented: false,
    });
    //@ts-ignore
    newBorderModeIndicator.set("borderSide", coords.border);
    canvas.add(newBorderModeIndicator);
    setBorderModeIndicator(newBorderModeIndicator);
  } else {
    borderModeIndicator.set({
      x1: indicatorCoords[0],
      x2: indicatorCoords[2],
      y1: indicatorCoords[1],
      y2: indicatorCoords[3],
    });
    //@ts-ignore
    borderModeIndicator.set("borderSide", coords.border);
    setBorderModeIndicator(borderModeIndicator);
  }
  canvas.renderAll();
};

// TOOLS ///////////

function getSegmentPointsNearMouse(
  polyObj: any,
  mouseX: number,
  mouseY: number,
  threshold: number
) {
  // threshold représente la distance maximale du curseur de la souris par rapport au segment

  const points = polyObj.get("points");
  const segmentPoints = [];

  for (let i = 0; i < points.length; i++) {
    const nextIndex = (i + 1) % points.length;
    const p1 = new fabric.Point(points[i].x, points[i].y);
    const p2 = new fabric.Point(points[nextIndex].x, points[nextIndex].y);
    const center = new fabric.Point((p1.x + p2.x) / 2, (p1.y + p2.y) / 2);

    // Calcul de la distance entre le centre du segment et le curseur de la souris
    const distance = Math.sqrt(
      Math.pow(center.x - mouseX, 2) + Math.pow(center.y - mouseY, 2)
    );

    // Si la distance est inférieure ou égale au seuil, on ajoute les points du segment
    if (distance <= threshold) {
      segmentPoints.push(p1, p2, nextIndex);
    }
  }
  return segmentPoints;
}

const _computeNewPointCoordinates = (extremities: any) => {
  const { x, y } = extremities[0];
  const newX = x - (x - extremities[1].x) / 2;
  const newY = y - (y - extremities[1].y) / 2;
  return { x: newX, y: newY };
};

export default mouseMoveHandler;
